// src/sections/WhyChooseUs.tsx
// Renamed from Features.tsx to match your file name

import React from 'react';
import { motion } from 'framer-motion';
import {
  FaPhoneAlt,
  FaBan,
  FaSearchLocation,
  FaExclamationTriangle,
  FaUserShield,
  FaShieldAlt,
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next'; // Import useTranslation

const featuresData = [ // Renamed to avoid conflict with 'features' variable in map
  {
    icon: <FaPhoneAlt className="text-white text-3xl" />,
    titleKey: 'feature_caller_id_title', // Translation key
    descriptionKey: 'feature_caller_id_description', // Translation key
  },
  {
    icon: <FaBan className="text-white text-3xl" />,
    titleKey: 'feature_spam_blocker_title',
    descriptionKey: 'feature_spam_blocker_description',
  },
  {
    icon: <FaSearchLocation className="text-white text-3xl" />,
    titleKey: 'feature_live_location_title',
    descriptionKey: 'feature_live_location_description',
  },
  {
    icon: <FaExclamationTriangle className="text-white text-3xl" />,
    titleKey: 'feature_scam_alert_title',
    descriptionKey: 'feature_scam_alert_description',
  },
  {
    icon: <FaUserShield className="text-white text-3xl" />,
    titleKey: 'feature_secure_private_title',
    descriptionKey: 'feature_secure_private_description',
  },
  {
    icon: <FaShieldAlt className="text-white text-3xl" />,
    titleKey: 'feature_live_caller_info_title',
    descriptionKey: 'feature_live_caller_info_description',
  },
];

// Define variants for the card animation
const cardVariants = {
  hidden: { opacity: 0, y: 100 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring" as "spring",
      stiffness: 100,
      damping: 10,
    },
  },
};

const WhyChooseUs: React.FC = () => {
  const { t } = useTranslation(); // Initialize useTranslation hook

  return (
    <section className="py-20" id="features">
      <div className="container mx-auto px-4">
        <motion.h2
          className="text-4xl font-bold text-center mb-12"
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
         {t('why_choose_heading_part1')}{' '} {/* Translated */}
         <span className="bg-gradient-to-r from-blue-600 to-green-500 bg-clip-text text-transparent">{t('brand_name')}</span> {/* Translated brand name (kept same for both languages) */}
         {t('why_choose_heading_part2')} {/* Translated "?" */}
        </motion.h2>

        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {featuresData.map((feature, index) => ( // Use featuresData here
            <motion.div
              key={index}
              className="group relative rounded-[2rem] bg-white p-0.5 transition-all hover:scale-105"
              // Use the defined variants
              variants={cardVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              transition={{ delay: index * 0.2 }} // Increased delay for a more noticeable staggered effect
            >
              {/* Fancy gradient border container */}
              <div className="absolute inset-0 rounded-[2rem] bg-gradient-to-br from-blue-600 to-green-500 opacity-0 group-hover:opacity-100 transition duration-300 z-0 blur-sm "></div>

              {/* App screen card UI */}
              <div className="relative z-10 bg-white rounded-[2rem] px-6 py-8 shadow-xl flex flex-col items-center text-center h-full">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-green-500 rounded-2xl flex items-center justify-center shadow-md mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">{t(feature.titleKey)}</h3> {/* Translated */}
                <p className="text-gray-600 text-sm">{t(feature.descriptionKey)}</p> {/* Translated */}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;